import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/ui/Modal';
import { analyticsConfigured, getConsent, initializeAnalytics, setConsent } from './analytics';

export function ConsentBanner(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [consent, setCurrentConsent] = useState(getConsent());
  useEffect(() => {
    initializeAnalytics();
    const show = () => {
      setCurrentConsent(getConsent());
      setOpen(analyticsConfigured());
    };
    window.addEventListener('skytech-privacy-settings', show);
    return () => window.removeEventListener('skytech-privacy-settings', show);
  }, []);
  if (!analyticsConfigured()) return null;
  const enabled = consent !== 'declined';
  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      label="Privacy settings"
      className="w-full max-w-md rounded-xl border border-frost bg-white p-5 shadow-hover sm:p-6"
      backdropClassName="bg-navy-900/55 backdrop-blur-[2px]"
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Close privacy settings"
        className="absolute right-3 top-3 flex size-11 items-center justify-center rounded-full text-2xl leading-none text-neutral transition-colors hover:bg-cloud hover:text-navy-900"
      >
        ×
      </button>
      <p className="pr-12 text-xs font-medium uppercase tracking-[0.18em] text-sky-ink">
        Your privacy
      </p>
      <h2 className="mt-2 pr-12 text-2xl font-light text-navy-900">Analytics settings</h2>
      <p className="mt-3 text-sm leading-relaxed text-neutral">
        We use privacy-minimised Google Analytics to improve product discovery and checkout.
        Advertising storage and personalisation stay off.
      </p>

      <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-frost bg-cloud px-4 py-3.5">
        <div>
          <p className="text-sm font-medium text-navy-900">Store analytics</p>
          <p className="mt-0.5 text-xs text-neutral" aria-live="polite">
            {enabled
              ? consent === 'accepted'
                ? 'On — helping us improve the store'
                : 'On in Kenya — regional consent rules still apply'
              : 'Off — optional events are blocked'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Store analytics"
          onClick={() => {
            const next = enabled ? 'declined' : 'accepted';
            setConsent(next);
            setCurrentConsent(next);
          }}
          className={`relative h-7 w-12 shrink-0 rounded-full border transition-colors ${
            enabled ? 'border-green-600 bg-green-600' : 'border-frost bg-frost'
          }`}
        >
          <span
            aria-hidden="true"
            className={`absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        <Link className="text-sm text-sky-ink underline underline-offset-4" to="/privacy">
          Read our privacy policy
        </Link>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="min-h-11 rounded-md bg-navy-900 px-5 text-sm font-medium text-white transition-colors hover:bg-navy-700"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}
